/**
 * Oak's Lab scene state machine (MVP PR A1).
 *
 * Cartridge-borrowed numbering from
 * pokefirered/data/maps/PalletTown_ProfessorOaksLab/scripts.inc's use of
 * VAR_MAP_SCENE_PALLET_TOWN_PROFESSOR_OAKS_LAB. Each value names a phase
 * of the lab cinematic; tap dialogue, coord-event eligibility, and post-
 * cinematic re-entry behavior all gate on it.
 *
 * State transitions:
 *
 *     ┌──────────────────────────────────────────────────────────────┐
 *     │                                                              │
 *     │   0  (pre-intercept)                                          │
 *     │   │                                                          │
 *     │   │  oakIntercept warp into lab                              │
 *     │   ▼                                                          │
 *     │   1  (Oak ready, monologue pending)                          │
 *     │   │                                                          │
 *     │   │  oakIntercept "Be patient" dialogue close                │
 *     │   ▼                                                          │
 *     │   2  (post-monologue, pre-pick, leave-guard active)          │
 *     │   │                                                          │
 *     │   │  labPostPick handler-end after rival takes opposing ball │
 *     │   ▼                                                          │
 *     │   3  (post-pick, rival-battle door triggers active)          │
 *     │   │                                                          │
 *     │   │  rivalLab post-battle handler-end                        │
 *     │   ▼                                                          │
 *     │   4  (sealed — cinematic complete, rival has exited)         │
 *     │                                                              │
 *     │   Reset Run wipes the var to 0; cinematic replays cleanly.   │
 *     │                                                              │
 *     └──────────────────────────────────────────────────────────────┘
 *
 * Pure helper, no Phaser deps. Consumed by labOakTap + labRivalTap
 * (state-aware tap dialogue), oakIntercept (writes states 1 and 2),
 * labPostPick (writes state 3), and rivalLab post-battle (writes state
 * 4).
 */
import { runState } from '../state/runState';
import { FLAGS } from './flagRegistry';
import { opposingStarterFor } from '../data/trainerParties';

/** Cartridge var name; same string is stored in runtime.json coord-event
 *  trigger entries that gate on `varValue: 2` (leave-guard) and
 *  `varValue: 3` (rival-battle). The value string format matches
 *  `runState.setVar` — string-keyed Record. */
export const VAR_LAB_SCENE = 'VAR_MAP_SCENE_PALLET_TOWN_PROFESSOR_OAKS_LAB';

/** Named state constants. Cartridge values 0-4 mapped 1:1; we don't
 *  use cartridge's 7/8/9 (National Dex scene, future feature). */
export const LAB_SCENE_STATES = {
  /** Pre-intercept: player hasn't triggered Oak on Route 1 yet. Lab is
   *  empty (all NPCs + balls hidden via INITIAL_HIDDEN_FLAGS). */
  PRE_INTERCEPT: 0,
  /** Oak's monologue hasn't fired yet but the player + Oak are in the
   *  lab. Cartridge transition window after warp + before "Be patient"
   *  close. Tap-rival in this window resolves to "Gramps! Fed up!". */
  MONOLOGUE_PENDING: 1,
  /** Oak's monologue + rival interjections complete. Player can tap
   *  balls to pick a starter. The runtime.json `LeaveStarterSceneTrigger`
   *  entries (varValue=2) activate here — walking south fires the
   *  leave-guard handler ("OAK: Hey! Don't go away yet!"). */
  PRE_PICK: 2,
  /** Player has picked, rival has walked to the opposing pedestal and
   *  taken his ball. The runtime.json `RivalBattleTrigger` entries
   *  (varValue=3) activate here — walking south fires rivalLab. */
  POST_PICK: 3,
  /** Rival defeated + walked out the south door. Lab is in its long-term
   *  post-cinematic state. No coord-event triggers active. */
  SEALED: 4,
} as const;

export type LabSceneState = (typeof LAB_SCENE_STATES)[keyof typeof LAB_SCENE_STATES];

/** Read the current lab scene state from runState. Defaults to 0 when
 *  the var is unset (fresh game, Reset Run). Coerces malformed strings
 *  to 0 defensively — any non-integer var value reads as pre-intercept,
 *  which is the safe fallback (cinematic re-fires cleanly). */
export function getLabSceneState(): LabSceneState {
  const raw = runState.getVar(VAR_LAB_SCENE);
  if (!raw) return 0;
  const n = Number(raw);
  if (n === 1 || n === 2 || n === 3 || n === 4) return n;
  return 0;
}

/** Write the lab scene state to runState. Handlers call this at beat
 *  boundaries (see state diagram in this file's header comment). The
 *  string conversion is explicit because runState.vars is
 *  `Record<string, string>`. */
export function setLabSceneState(state: LabSceneState): void {
  runState.setVar(VAR_LAB_SCENE, String(state));
}

/**
 * Repair a save stranded mid-post-pick-cinematic.
 *
 * The labPostPick handler latches LAB_POST_PICK_DONE at ENTRY (re-
 * entrancy guard) and only advances the scene var to POST_PICK at the
 * very END. A session that dies between the two — the pre-2026-08-01
 * walker deadlock froze exactly there when the rival's scripted path
 * crossed the input-locked player's tile, but a tab-close mid-cinematic
 * hits the same window — reloads into an unwinnable state: the latch
 * blocks the cinematic from re-firing while state 2 keeps the
 * leave-guard active, trapping the player in the lab forever.
 *
 * Reconstructs the post-cinematic invariants idempotently: state → 3,
 * static rival visible at his spawn, opposing-type ball hidden. Called
 * from WorldScene's lab map-load path (covers boot-resume AND
 * re-entry) BEFORE NPC sprites mount so visibility resolves against
 * the repaired flags. Returns true when a repair was applied so the
 * caller can persist.
 *
 * LEGACY-ONLY since the third-playtest latch fix (2026-08-02): both
 * lab cinematics now persist their DONE latch at the END (in-memory
 * guards handle session re-entrancy), so a NEW save can never carry
 * LAB_POST_PICK_DONE with state still at PRE_PICK. Kept because it's
 * cheap, idempotent, and rescues any save written under the old
 * entry-latch code.
 */
export function repairStrandedLabPostPick(): boolean {
  if (!runState.hasFlag(FLAGS.STARTER_PICKED)) return false;
  if (!runState.hasFlag(FLAGS.LAB_POST_PICK_DONE)) return false;
  if (getLabSceneState() !== LAB_SCENE_STATES.PRE_PICK) return false;

  setLabSceneState(LAB_SCENE_STATES.POST_PICK);
  runState.clearFlag(FLAGS.HIDE_RIVAL_IN_LAB);
  const starter = runState.getParty()[0]?.species;
  const opposing = starter ? opposingStarterFor(starter)?.species : undefined;
  if (opposing === 'bulbasaur') runState.setFlag(FLAGS.HIDE_BULBASAUR_BALL);
  else if (opposing === 'charmander') runState.setFlag(FLAGS.HIDE_CHARMANDER_BALL);
  else if (opposing === 'squirtle') runState.setFlag(FLAGS.HIDE_SQUIRTLE_BALL);
  return true;
}
